import React, { useState } from "react";

import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  MoreHoriz as MoreHorizIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Portal,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import { ProgramDuplicateDialogFragment } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import DuplicateDialog from "./DuplicateDialog";
import PDFSectionsDialog from "../../components/PDFSectionsDialog";
import { PDFSectionsProp } from "../../RenderPDF/PDFDocuments";
import { downloadPDF } from "../../RenderPDF/utils/download";

interface Props {
  program: ProgramDuplicateDialogFragment;
  onDelete: () => Promise<void>;
}

const useStyles = makeStyles()((theme) => ({
  deleteDialogPaper: {
    minWidth: 400,
    maxWidth: 400,
  },
  deleteDialogContent: {
    paddingTop: 0,
  },
  deleteDialogIcon: {
    maxHeight: 60,
    fontSize: 60,
    color: colors.secondary,
  },
  deleteDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },
  deleteDialogMessage: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "22px",
    color: colors.gray,
    textAlign: "center",
  },
  deleteDialogButton: {
    minWidth: 125,
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: theme.spacing(24, 56),
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(2, 2),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(2, 2, 0, 2),
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(8),
    marginBottom: theme.spacing(1),
  },
  saveButton: {
    marginLeft: "18px",
    backgroundColor: colors.primary,
    borderRadius: "32px",
    paddingInline: "24px",
    marginBottom: theme.spacing(7),
  },
  datePicker: {
    marginTop: "8px",
    marginBottom: "8px",
  },
  caption: {
    fontSize: "12px",
  },
  icon: {
    color: colors.primary,
    marginRight: 8,
    width: 21,
    height: 21,
  },
}));

const OptionMenu: React.FC<Props> = ({ program, onDelete }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const deleteDialog = useDialogState();
  const duplicateDialog = useDialogState();
  const previewPdfDialog = useDialogState();
  const downloadPdfDialog = useDialogState();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const closeDeleteDialog = () => {
    setIsDeleting(false);
    deleteDialog.hide();
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    onDelete().then(closeDeleteDialog);
  };

  const handlePreviewPDF = (sections: PDFSectionsProp) => {
    previewPdfDialog.hide();
    const sectionParams = new URLSearchParams(sections as any).toString();
    window.open(`programs/${program.id}/pdf?${sectionParams}`, "_blank");
  };

  const handleDownloadPDF = async (sections: PDFSectionsProp) => {
    downloadPdfDialog.hide();
    try {
      await downloadPDF(program.id, sections);
    } catch (e) {
      snackbar.open(
        displayError(e, "Unable to save as PDF. Please try again later.")
      );
    }
  };

  return (
    <Box display="flex" flexDirection="row" marginLeft={1} marginRight={1}>
      <IconButton onClick={handleOpen} size="large">
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem component={Link} to={`${program.id}/edit`}>
          <EditIcon className={classes.icon} />
          <Typography variant="body2" color="textPrimary">
            Edit
          </Typography>
        </MenuItem>
        <MenuItem onClick={duplicateDialog.show}>
          <FileCopyIcon className={classes.icon} />
          <Typography variant="body2" color="textPrimary">
            Duplicate
          </Typography>
        </MenuItem>
        <MenuItem onClick={deleteDialog.show}>
          <DeleteForeverIcon className={classes.icon} />
          <Typography variant="body2" color="textPrimary">
            Delete
          </Typography>
        </MenuItem>
        <MenuItem onClick={previewPdfDialog.show}>
          <PictureAsPdfIcon className={classes.icon} />
          <Typography variant="body2" color="textPrimary">
            Preview PDF
          </Typography>
        </MenuItem>
        <MenuItem onClick={downloadPdfDialog.show}>
          <PictureAsPdfIcon className={classes.icon} />
          <Typography variant="body2" color="textPrimary">
            Download PDF
          </Typography>
        </MenuItem>
      </Menu>
      <Portal>
        <Dialog
          {...deleteDialog.props}
          classes={{ paper: classes.deleteDialogPaper }}
          contentClassName={classes.deleteDialogContent}
          content={
            <Box display="flex" flexDirection="column" alignItems="center">
              <div className={classes.deleteDialogIcon}>
                <WarningIcon fontSize="inherit" color="inherit" />
              </div>
              <Box marginBottom={5} />
              <Typography className={classes.deleteDialogTitle}>
                Are you sure?
              </Typography>
              <Box marginBottom={2} />
              <Typography className={classes.deleteDialogMessage}>
                Do you really want to delete this plan?
              </Typography>
            </Box>
          }
          actions={
            <Button
              size="large"
              className={classes.deleteDialogButton}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size="1.6rem" /> : <>Confirm</>}
            </Button>
          }
        />
      </Portal>
      <DuplicateDialog {...duplicateDialog.props} program={program} />
      <PDFSectionsDialog
        {...previewPdfDialog.props}
        title="Preview PDF"
        buttonLabel="Preview"
        onSubmit={handlePreviewPDF}
      />
      <PDFSectionsDialog
        {...downloadPdfDialog.props}
        title="Download as PDF"
        buttonLabel="Download"
        onSubmit={handleDownloadPDF}
      />
    </Box>
  );
};

export default OptionMenu;
